import { readActiveStorefrontSearchParams } from "~/app/router/activeStorefrontRouter";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { useSelector } from "react-redux";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useHoverSelection } from "~/common/hooks/useHoverSelection";
import { selectNavigationNodes, selectRootNode } from "~/features/navigation/redux/selectors";
import type { NavigationNode, TreeNode } from "~/domains/navigation/types/NavigationTypes";
import { useNavigationState } from "~/features/categoryNavigation/hooks/useNavigationState";
import { useDesktopDrawerOffsets } from "~/features/categoryNavigation/hooks/useDesktopDrawerOffsets";
import { DesktopNavigation } from "~/features/categoryNavigation/components/DesktopNavigation";
import { MobileNavigation } from "~/features/categoryNavigation/components/MobileNavigation";
import { NAVIGATION_HOVER_DELAY } from "~/utils/uiConstants";

export const CategoryNavigation = ({ navigationRef }: { navigationRef: React.RefObject<HTMLElement | null> }) => {
  const desktopNavRef = useRef<HTMLDivElement>(null);

  let delay = NAVIGATION_HOVER_DELAY;
  // Task 0231 slice 4: the test-hook override reads the booted router's raw
  // history location (render-time like before).
  const navDelay = readActiveStorefrontSearchParams()?.get("navDelay") ?? null;

  if (navDelay !== null) {
    delay = parseInt(navDelay, 10);
  }

  const screenSize = useNumericScreenSize();
  const isDesktop = screenSize >= useNumericScreenSize.desktopS;

  const navigationNodes = useSelector(selectNavigationNodes);
  const rootNode = useSelector(selectRootNode);
  const tree = useMemo(() => {
    return buildNavigationTree(rootNode, navigationNodes);
  }, [navigationNodes, rootNode]);

  const { nodesInTransition, isOpen, selectNode, goBack, close, closeOnly, clearNodes, toggleOpen } =
    useNavigationState(tree);
  const { onMouseEnter, onMouseLeave } = useHoverSelection(selectNode, delay);

  const locationHref = useRouterState({ select: (state) => state.location.href });

  useEffect(() => {
    onMouseLeave();
    closeOnly();
  }, [locationHref, tree, onMouseLeave, closeOnly]);

  const offsetStyles = useDesktopDrawerOffsets(navigationRef, desktopNavRef);

  if (isDesktop) {
    return (
      <div ref={desktopNavRef}>
        <DesktopNavigation
          tree={tree}
          nodesInTransition={nodesInTransition}
          isOpen={isOpen}
          offsetStyles={offsetStyles}
          selectNode={selectNode}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          close={closeOnly}
          onExited={clearNodes}
        />
      </div>
    );
  }

  return (
    <div ref={desktopNavRef}>
      <MobileNavigation
        tree={tree}
        nodesInTransition={nodesInTransition}
        isOpen={isOpen}
        delay={delay}
        offsetStyles={offsetStyles}
        selectNode={selectNode}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        close={close}
        goBack={goBack}
        toggleOpen={toggleOpen}
      />
    </div>
  );
};

export function buildNavigationTree(root: NavigationNode, nodes: Record<number, NavigationNode>): TreeNode {
  const buildNode = (node: NavigationNode): TreeNode => {
    return {
      ...node,
      childNodes: node.children
        .map((childId) => nodes[childId])
        .filter((childNode): childNode is NavigationNode => childNode !== undefined)
        .filter((childNode) => childNode.level <= 3)
        .map((childNode) => buildNode(childNode)),
    };
  };

  return buildNode(root);
}
